import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Mail, Lock, Eye, EyeOff, CheckCircle, ShieldCheck, FileCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { homePathForRole, canAccessPath } from '../utils/roleHome';
import './AuthPage.css';

export default function LoginPage() {
  const { t } = useLanguage();
  const { login, demoAccounts, user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '';

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      navigate(homePathForRole(user.role) || '/', { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email.trim() || !form.password) {
      setError(t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    const result = await login(form.email, form.password);
    if (!result.ok) {
      setError(t.auth[result.error] || t.auth.invalidCredentials);
      return;
    }
    const role = result.user?.role || 'client';
    const target =
      from && canAccessPath(role, from) ? from : homePathForRole(role);
    navigate(target, { replace: true });
  };

  const fillDemo = (account) => {
    setForm({ email: account.email, password: account.password });
    setError('');
  };

  const roleLabel = {
    client: t.dashboard.roleClient,
    translator: t.dashboard.roleTranslator,
    admin: t.dashboard.roleAdmin,
  };

  const perks = [
    { icon: ShieldCheck, text: t.auth.perkSecure },
    { icon: FileCheck, text: t.auth.perkOrders },
    { icon: CheckCircle, text: t.auth.perkDocs },
  ];

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <aside className="auth-brand-panel">
          <div className="auth-brand-glow" aria-hidden="true" />
          <div className="auth-brand-content">
            <p className="auth-brand-name">
              Tarjemlek
              <span lang="ar">ترجملك</span>
            </p>
            <h1>{t.auth.loginTitle}</h1>
            <p className="auth-brand-text">{t.auth.loginSubtitle}</p>
            <ul className="auth-perks">
              {perks.map(({ icon: Icon, text }) => (
                <li key={text}>
                  <Icon size={18} aria-hidden="true" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="auth-form-panel">
          <form className="auth-card" onSubmit={handleSubmit} noValidate>
            <div className="auth-card-head">
              <h2>{t.auth.welcomeBack}</h2>
              <p>{t.auth.loginFormHint}</p>
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}

            <div className="auth-field">
              <label htmlFor="login-email">{t.auth.email}</label>
              <div className="auth-input">
                <Mail size={17} aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="email@exemple.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="login-password">{t.auth.password}</label>
              <div className="auth-input">
                <Lock size={17} aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <p style={{ margin: '0 0 12px', textAlign: 'end' }}>
              <Link to="/forgot-password">{t.auth.forgotLink}</Link>
            </p>

            <button type="submit" className="btn btn-primary auth-submit">
              <LogIn size={18} />
              {t.auth.loginButton}
            </button>

            <div className="auth-demo">
              <p className="auth-demo-title">{t.dashboard.demoHint}</p>
              <div className="auth-demo-list">
                {demoAccounts.map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    className="auth-demo-chip"
                    onClick={() => fillDemo(account)}
                  >
                    <strong>{roleLabel[account.role]}</strong>
                    <span>{account.email}</span>
                  </button>
                ))}
              </div>
            </div>

            <p className="auth-switch">
              {t.auth.noAccount}{' '}
              <Link to="/register" state={{ from }}>{t.auth.registerLink}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
